import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronDown, ClipboardCheck, PlayCircle, Rocket, SlidersHorizontal, UtensilsCrossed, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, xxLargeTextSize } from '../../../styling/stylingHelper';
import { saveCurrentScrollPosition, restoreScrollPosition } from '../../../utils/scrollUtils';
import { formatPlainDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../services/authentication/authContext';
import { canAccessMenuChangeDashboard } from '../../../services/accessManagement/accessManagement';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import {
  CardStatus,
  DashboardEntry,
} from '../../../services/menuChange/menuChangeService';
import MenuChangeTimeline from './MenuChangeTimeline';
import MenuChangeActions from './MenuChangeActions';
import RestaurantLogo from '../../ui/RestaurantLogo';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import TaskReminder from './TaskReminder';

const STATUS_FILTERS: (CardStatus | 'all')[] = ['all', 'live', 'blocked', 'scheduled', 'incomplete', 'complete', 'inactive'];
const ITEMS_PER_PAGE = 9;

// Status badge — pill matching the mock's LocationCard StatusBadge.
const STATUS_BADGE: Record<CardStatus, string> = {
  live: 'bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#18B081]',
  complete: 'bg-[#18B081] dark:bg-[#18B081] text-white dark:text-white',
  incomplete: 'bg-[#F04438] dark:bg-[#F04438] text-white dark:text-white',
  blocked: 'bg-[#F04438] text-white',
  scheduled: 'bg-[#EFF8FF] text-[#2E90FA] dark:bg-[#14344C]',
  inactive: 'text-black dark:text-[#94A3B8] bg-[#F0F1F1] dark:bg-[#1E3A52]',
};

// Urgency ranking for the grid: what needs attention soonest comes first.
// Within a rank, the earliest go-live wins (goLiveDate is a plain YYYY-MM-DD
// string, so a lexical compare is a date compare).
const STATUS_RANK: Record<CardStatus, number> = {
  blocked: 0,
  incomplete: 0,
  live: 1,
  scheduled: 2,
  complete: 3,
  inactive: 4,
};

const MenuChangeDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();
  const canManage = canAccessMenuChangeDashboard(backendUser, accountSettings);

  const loadRestaurants = useRestaurantStore((s) => s.loadAllData);
  const selectedRestaurant = useAppUserDefaultStore((s) => s.restaurant);

  const dashboard = useMenuChangeStore((s) => s.dashboard);
  const dashboardLoaded = useMenuChangeStore((s) => s.dashboardLoaded);
  const loadDashboard = useMenuChangeStore((s) => s.loadDashboard);

  const [statusFilter, setStatusFilter] = useState<CardStatus | 'all'>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadRestaurants();
    loadDashboard();
  }, [loadRestaurants, loadDashboard]);

  // Restore scroll when returning from a timeline detail page (orderhistory pattern).
  useEffect(() => {
    restoreScrollPosition();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setIsFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // "Live" covers blocked as well — a blocked menu change is still running, it
  // just needs attention. "Blocked" stays the narrow view of only those.
  const filtered = useMemo(
    () =>
      dashboard
        .filter(
          (e) =>
            statusFilter === 'all'
            || e.status === statusFilter
            || (statusFilter === 'live' && e.status === 'blocked')
        )
        .sort((a, b) => {
          const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status];
          if (rank !== 0) return rank;
          const aDate = a.activeRollout?.goLiveDate ?? '9999-12-31';
          const bDate = b.activeRollout?.goLiveDate ?? '9999-12-31';
          if (aDate !== bDate) return aDate < bDate ? -1 : 1;
          return a.restaurantName.localeCompare(b.restaurantName);
        }),
    [dashboard, statusFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paged = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Stat tiles derived from the dashboard entries.
  const stats = useMemo(() => {
    const live = dashboard.filter((e) => e.status === 'live' || e.status === 'blocked').length;
    const complete = dashboard.filter((e) => e.status === 'complete').length;
    const inactive = dashboard.filter((e) => e.status === 'inactive').length;
    // Earliest FUTURE go-live among active rollouts (goLiveDate is a plain
    // YYYY-MM-DD string, so lexical compare against today works).
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = dashboard
      .map((e) => e.activeRollout?.goLiveDate)
      .filter((d): d is string => !!d && d >= today)
      .sort();
    return { live, complete, inactive, nearestLaunch: upcoming[0] ?? null };
  }, [dashboard]);

  const goToTimeline = (restaurantId: string) => {
    saveCurrentScrollPosition();
    navigate(`/menus/change-dashboard/${restaurantId}`);
  };

  // The cross-restaurant grid belongs to "All restaurants" only. With a specific
  // restaurant selected you get that restaurant's timeline and nothing else —
  // this also covers landing on the overview URL directly.
  if (selectedRestaurant && !isAllRestaurantsSelected(selectedRestaurant)) {
    return <Navigate to={`/menus/change-dashboard/${selectedRestaurant.id}`} replace />;
  }

  // A single accessible restaurant → skip the grid, show the timeline inline.
  const onlyRestaurant = dashboardLoaded && dashboard.length === 1
    ? { id: dashboard[0].restaurantId, name: dashboard[0].restaurantName, logoUrl: dashboard[0].restaurantLogoUrl }
    : null;

  if (onlyRestaurant) {
    return (
      <div>
        <MobilePageHeader title={t('menuChange.title')} right={<RestaurantSelector />} />
        <h2 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'pb-6 hidden lg:block')}>{t('menuChange.title')}</h2>
        <div className="flex justify-end mb-4"><MenuChangeActions key={onlyRestaurant.id} restaurantId={onlyRestaurant.id} /></div>
        <div className="bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark p-4 sm:p-6">
          <MenuChangeTimeline key={onlyRestaurant.id} restaurantId={onlyRestaurant.id} restaurantName={onlyRestaurant.name} restaurantLogoUrl={onlyRestaurant.logoUrl} canManage={canManage} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <MobilePageHeader title={t('menuChange.title')} right={<RestaurantSelector />} />

      {/* Page title + actions */}
      <div className="w-full flex flex-col gap-4 min-[1050px]:flex-row min-[1050px]:justify-between min-[1050px]:items-center">
        <h1 className={cn('text-[24px] leading-[30px] font-bold', primaryText, 'hidden lg:block')}>{t('menuChange.title')}</h1>

        <MenuChangeActions />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <StatCard tint="#18B081" icon={<PlayCircle className="w-6 h-6" style={{ color: '#18B081' }} />} title={t('menuChange.stats.liveCycles')} value={String(stats.live).padStart(2, '0')} />
        <StatCard tint="#F1A62D" icon={<ClipboardCheck className="w-6 h-6" style={{ color: '#F1A62D' }} />} title={t('menuChange.stats.completed')} value={String(stats.complete).padStart(2, '0')} />
        <StatCard tint="#F04438" icon={<UtensilsCrossed className="w-6 h-6" style={{ color: '#F04438' }} />} title={t('menuChange.stats.inactive')} value={String(stats.inactive).padStart(2, '0')} />
        <StatCard tint="#2E90FA" icon={<Rocket className="w-6 h-6" style={{ color: '#2E90FA' }} />} title={t('menuChange.stats.nearestLaunch')} value={stats.nearestLaunch ? formatPlainDate(stats.nearestLaunch) : '—'} />
      </div>

      {/* All locations card */}
      <div className="bg-white dark:bg-background-dark rounded-xl shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] border border-transparent dark:border-borderPrimary-dark p-3.5 sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <h2 className={cn('text-[18px] sm:text-[24px] leading-6 sm:leading-[32px] font-semibold', primaryText)}>
            {t('menuChange.allLocations')}
          </h2>

          {/* Status filter — mock FilterDropdown styling */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setIsFilterOpen((v) => !v)}
              className="flex items-center gap-2 h-11 px-3 rounded-xl bg-white dark:bg-[#14344C] border border-[#D0D5DD] dark:border-[#2D4F68] shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040] hover:bg-[#F9FAFB] dark:hover:bg-[#14344C]/80 transition-colors"
            >
              <SlidersHorizontal className="w-5 h-5 text-[#344054] dark:text-[#CBD5E1]" />
              <span className="text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#CBD5E1] whitespace-nowrap">
                {t(`menuChange.filter.${statusFilter}`)}
              </span>
              <ChevronDown className={cn('w-5 h-5 text-[#344054] dark:text-[#CBD5E1] transition-transform', isFilterOpen && 'rotate-180')} />
            </button>

            {isFilterOpen && (
              <div className="absolute top-[calc(100%+4px)] right-0 z-30 w-[180px] bg-white dark:bg-[#0E2B42] border border-[#F5F5F5] dark:border-[#1E3A4E] rounded-xl shadow-[0px_8px_10px_rgba(0,0,0,0.07)] dark:shadow-[0px_8px_10px_rgba(0,0,0,0.35)] p-2 flex flex-col gap-1">
                {STATUS_FILTERS.map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setPage(1);
                      setIsFilterOpen(false);
                    }}
                    className={cn(
                      'flex items-center justify-between px-3 py-2 rounded-lg text-[14px] leading-5 font-medium text-left transition-colors',
                      statusFilter === status
                        ? 'bg-[#EDFCF5] dark:bg-[#0E8965]/20 text-[#0E8965]'
                        : 'hover:bg-gray-50 dark:hover:bg-[#14344C] text-[#344054] dark:text-[#CBD5E1]'
                    )}
                  >
                    {t(`menuChange.filter.${status}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {paged.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-2 gap-y-3">
            {paged.map((entry) => (
              <RestaurantCard key={entry.restaurantId} entry={entry} onViewTimeline={() => goToTimeline(entry.restaurantId)} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center text-[14px] text-[#667085] dark:text-[#94A3B8]">
            {t('menuChange.noRestaurantsForFilter')}
          </div>
        )}

        {filtered.length > ITEMS_PER_PAGE && (
          <Pagination page={page} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

// Matches the main dashboard StatCard treatment (cardClass wrapper + 42×42
// rounded-[8px] icon chip tinted with the icon color at 1A alpha), with the
// mock's icon per tile.
const StatCard: React.FC<{ tint: string; icon: React.ReactNode; title: string; value: string }> = ({ tint, icon, title, value }) => (
  <div className={cn(cardClass, 'p-5 flex flex-col h-full')}>
    <div className="flex flex-col items-start gap-2 mb-3">
      <span
        className="flex items-center justify-center w-[42px] h-[42px] rounded-[8px] flex-shrink-0"
        style={{ backgroundColor: `${tint}1A` }}
      >
        {icon}
      </span>
      <span className={cn(secondaryText, 'text-lg font-semibold')}>{title}</span>
    </div>
    {/* break-words: the nearest-launch tile carries a formatted date, not a 2-digit count. */}
    <span className={cn(primaryText, 'text-3xl font-bold break-words')}>{value}</span>
  </div>
);

// ---------------------------------------------------------------------------

const RestaurantCard: React.FC<{ entry: DashboardEntry; onViewTimeline: () => void }> = ({ entry, onViewTimeline }) => {
  const { t } = useTranslation();
  const loadReminders = useMenuChangeStore(s => s.loadReminders);
  const rollout = entry.activeRollout;
  useEffect(() => {
    if (rollout) void loadReminders(rollout.id, true);
  }, [rollout, loadReminders]);
  const isInactive = entry.status === 'inactive';
  const isBlocked = entry.status === 'blocked' || entry.status === 'incomplete';
  const isScheduled = entry.status === 'scheduled';
  const tasks = rollout?.currentWeekTasks.slice(0, 3) ?? [];

  const barColor = isBlocked ? 'bg-[#F04438] dark:bg-[#F04438]' : isInactive || isScheduled ? 'bg-[#85888E] dark:bg-[#85888E]' : 'bg-[#0E8965] dark:bg-[#0E8965]';

  return (
    <div
      onClick={() => !isInactive && onViewTimeline()}
      className={cn(
        'bg-white dark:bg-[#0E2B42] rounded-[10px] p-3 sm:p-4 flex flex-col shadow-[0px_1px_2px_0px_#1823220D] dark:shadow-[0px_1px_2px_0px_#00000040] border transition-shadow',
        isBlocked ? 'border-[#F04438] dark:border-[#F04438]' : 'border-[#EDF1F1] dark:border-[#1E3A4E]',
        // The whole card is the way into the timeline — that is where every edit happens.
        !isInactive && 'cursor-pointer hover:shadow-[0px_4px_12px_0px_#18232214] dark:hover:shadow-[0px_4px_12px_0px_#00000059]'
      )}
    >
      {/* name + status badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <RestaurantLogo logoUrl={entry.restaurantLogoUrl} name={entry.restaurantName} size="small" />
          <span className="text-[18px] font-semibold text-[#0C111D] dark:text-[#F8FAFC] leading-[28px] truncate min-w-0">
            {entry.restaurantName}
          </span>
        </div>
        <span
          className={cn(
            'text-[14px] leading-5 font-medium px-3 h-6 rounded-full flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0',
            STATUS_BADGE[entry.status]
          )}
        >
          {isBlocked && <AlertTriangle className="w-4 h-4" />}
          {t(`menuChange.status.${entry.status}`)}
        </span>
      </div>

      {rollout ? (
        <>
          {/* go-live date */}
          <div className="my-3 flex items-center gap-1 text-[12px] leading-[18px] font-medium text-[#333741] dark:text-[#94A3B8]">
            <Calendar className="w-5 h-5" />
            <span>{t('menuChange.goLive')}: {formatPlainDate(rollout.goLiveDate)}</span>
          </div>

          {/* week progress */}
          <div className="flex flex-col">
            <div className="flex items-center justify-between">
              <span className="text-[12px] leading-[18px] font-medium text-[#333741] dark:text-[#94A3B8]">
                {rollout.currentWeekPos === 0
                  ? t('menuChange.notStartedOn', { date: formatPlainDate(rollout.startDate) })
                  : t('menuChange.weekOf', { current: rollout.currentWeekPos, total: rollout.numWeeks })}
              </span>
              <span className="text-[12px] leading-[18px] font-medium text-[#333741] dark:text-[#F8FAFC]">{rollout.progress}%</span>
            </div>
            <div className="my-1.5 w-full h-2 bg-[#EAECF0] dark:bg-[#1E3A52] rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${Math.min(rollout.progress, 100)}%` }} />
            </div>
          </div>

          {/* this week's to-dos */}
          <div className={cn('rounded-xl p-3 flex flex-col', isBlocked ? 'bg-[#FEF3F2] dark:bg-[#D92D20]/10 mt-2' : 'bg-[#F9FAFB] dark:bg-[#0A1F30] mt-3')}>
            <div className="flex items-center justify-between mb-3">
              <span className={cn('text-[12px] leading-[18px] font-semibold uppercase', isBlocked ? 'text-[#B42318]' : 'text-[#0C111D] dark:text-[#94A3B8]')}>
                {t('menuChange.thisWeeksTasks')}
              </span>
              <span className={cn('rounded-[20px] px-2.5 h-5 text-[12px] leading-5 font-medium flex items-center', isBlocked ? 'bg-white text-[#F04438]' : 'bg-[#F0F1F1] dark:bg-[#1E3A52] text-[#0C111D] dark:text-[#F8FAFC]')}>
                {tasks.length}
              </span>
            </div>

            <div className="space-y-2.5">
              {tasks.length === 0 ? (
                <p className="text-[14px] leading-5 text-[#667085] dark:text-[#94A3B8]">{t('menuChange.noTasksThisWeek')}</p>
              ) : (
                tasks.map((task) => (
                  <div key={task.taskId}>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'w-5 h-5 rounded-[6px] flex-shrink-0 border flex items-center justify-center',
                          task.done
                            ? 'bg-[#0E8965] dark:bg-[#0E8965] border-[#0E8965]'
                            : isBlocked
                              ? 'border-[#F04438]/50'
                              : 'border-[#D0D5DD] dark:border-[#2D4F68]'
                        )}
                      >
                        {task.status === 'done' && <CheckMark />}
                        {task.status === 'not_done' && <X className="w-3 h-3 text-[#F04438] dark:text-textRed-dark" strokeWidth={3} aria-label={t('menuChange.taskStatus.not_done')} />}
                      </span>
                      {!task.assigneeUserId && !task.done && <AlertTriangle className="w-4 h-4 text-[#F04438] dark:text-textRed-dark flex-shrink-0" />}
                      <span
                        className={cn(
                          'text-[14px] leading-5 font-medium truncate',
                          task.done ? 'line-through text-[#0C111D] dark:text-[#64748B]' : 'text-[#0C111D] dark:text-[#CBD5E1]'
                        )}
                      >
                        {task.label}
                      </span>
                    </div>
                    {task.status !== 'done' && <TaskReminder rolloutId={rollout.id} taskId={task.taskId} />}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="my-3 text-[14px] leading-5 text-[#667085] dark:text-[#94A3B8]">{t('menuChange.noActiveRollout')}</p>
      )}

      {/* View timeline */}
      <button
        disabled={isInactive}
        onClick={(e) => {
          e.stopPropagation();
          if (!isInactive) onViewTimeline();
        }}
        className={cn(
          'mt-3 w-full h-10 rounded-md border flex items-center justify-center gap-2 text-[14px] leading-[22px] font-medium transition',
          isInactive
            ? 'border-[#E4E5E1] dark:border-[#1E3A4E] bg-[#F6F6F6] dark:bg-[#0A1F30] text-[#94969C] cursor-not-allowed'
            : isBlocked
              ? 'border-[#FECDCA] text-[#B42318] bg-[#FEF3F2] hover:bg-[#FEF3F2]/80 dark:hover:bg-[#D92D20]/10'
              : 'border-[#E4E5E1] bg-[#F6F6F6] dark:border-transparent dark:bg-[#0A1F30] text-black dark:text-[#F8FAFC] hover:text-white hover:bg-[#18B081]'
        )}
      >
        <TimelineIcon />
        {t('menuChange.viewTimeline')}
      </button>
    </div>
  );
};

const CheckMark: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.2 1L4.5 8.7L1 5.2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TimelineIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 17H12V15H6V17ZM9 13H15V11H9V13ZM12 9H18V7H12V9ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V5H5V19Z" fill="currentColor" />
  </svg>
);

// ---------------------------------------------------------------------------

interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const { t } = useTranslation();
  const startIndex = (page - 1) * itemsPerPage;
  const start = startIndex + 1;
  const end = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <div className="mt-4 flex items-center justify-between pt-4 border-t border-[#EAECF0] dark:border-[#FFFFFF14]">
      <span className="text-[13px] text-[#667085] dark:text-[#858D9D]">
        {t('menuChange.pagination', { start, end, total: totalItems })}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#0E8965] dark:border-[#0E8965] text-[#0E8965] dark:text-[#0E8965] disabled:opacity-40 disabled:cursor-not-allowed transition"
          aria-label={t('common.previousPage')}
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-lg text-[14px] leading-5 font-semibold transition',
              page === p
                ? 'bg-[#0E8965] text-white'
                : 'border border-[#EAECF0] dark:border-[#FFFFFF14] text-[#858D9D] dark:text-[#CBD5E1] hover:bg-gray-50 dark:hover:bg-[#858D9D]/50'
            )}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#0E8965] dark:border-[#0E8965] text-[#0E8965] dark:text-[#0E8965] disabled:opacity-40 disabled:cursor-not-allowed transition"
          aria-label={t('common.nextPage')}
        >
          <ChevronDown className="w-4 h-4 -rotate-90" />
        </button>
      </div>
    </div>
  );
};

export default MenuChangeDashboardPage;
